/*
    Creator site - Curriculum Edit Page
    Allows the creator of a previously created curriculum to edit it
*/

import React from 'react'
import { Tabs, Tab, TabPanel, TabList } from 'react-web-tabs'
import 'react-web-tabs/dist/react-web-tabs.css'

import * as Config from './config'
import * as Database from './database-api'
import * as Fileserver from './fileserver-api'
import * as CloudStorage from './cloud-storage-api'
import * as Utility from './utility'
import * as CreatorUtility from './creator-utility'

const globals = Config.globals
const fsConfig = Config.fsConfig
const creatorConfig = Config.pages.creator
const curriculumConfig = Config.curriculumConfig
const generalConfig = Config.pages.general
const payConfig = Config.payConfig

class CreatorEditCurriculumPage extends CreatorUtility.CreatorRestrictedAccessLandingPage {
    constructor(props) {
        super(props)
        this.pageId = 'creator-edit'
        this.hasAwayNavigationPrompt = true
        this.imageUploaded = false        
        this.previewsUploaded = false
        this.curriculumProofUploaded = {school: false, teaching: false}
        this.uploadDropZoneNewMessage = 'Drop @ file here, or click to select...'
        this.uploadDropZoneEditMessage = '✓ File uploaded. To change it, drop @ file here or click to select...'
        this.curriculumImagePlaceHolder = generalConfig.curriculumImagePlaceHolder        
        this.descriptionText = ''
        this.numLessonResourcesCounted = 0
        this.curriculumFilesUploadedNames = {}
        this.topCTAComponentRef = React.createRef()
        this.topicSelectComponentRef = React.createRef()
        this.formTabsComponent = React.createRef()
        this.formDescriptionTextEditor = React.createRef()
        this.curriculumFormLearnWhat = React.createRef()
        this.curriculumFormSearchTerms = React.createRef()
        this.curriculumFormTeacherRequirements = React.createRef()
        this.curriculumFormStudentRequirements = React.createRef()        
        this.curriculumFormCoursePlan = React.createRef()
        this.curriculumFormResources = React.createRef()
        this.formProposedTermYearInputRef = React.createRef()
        this.formProposedTopicInputRef = React.createRef()
        this.formProposedSubjectInputRef = React.createRef()
        this.imageUploadZoneRef = React.createRef()
        this.previewsUploadZoneRef = React.createRef()
        this.schoolProofUploadZoneRef = React.createRef()
        this.teachingProofUploadZoneRef = React.createRef()
        this.otherProofsRefs = React.createRef()
        this.promosRefs = React.createRef()
        this.formCurriculumTypeRef = React.createRef()
        this.formHasInstructionVideoRef = React.createRef()
        this.formTitleRef = React.createRef()
        this.formSubtitleRef = React.createRef()
        this.formMinGradeRef = React.createRef()
        this.formMaxGradeRef = React.createRef()
        this.formLanguageRef = React.createRef()
        this.formProposedLanguageInputRef = React.createRef()
        this.formPriceTierRef = React.createRef()
        this.formNumberOfWeeksRef = React.createRef()
        this.formNumberOfLessonsPerWeekRef = React.createRef()
        this.formNumberOfMinutesPerLessonRef = React.createRef()
        this.formNumberOfTestsRef = React.createRef()
        this.formSchoolNameRef = React.createRef()
        this.formSchoolWebsiteRef = React.createRef()
        this.formTermNameRef = React.createRef()
        this.formTeacherNotes = React.createRef()
        this.otherProofFilesUploadedNames = {}
        if (globals.haveLocalStorage)
            this.lastTabOpen = localStorage.getItem('termeric-page-' + this.pageId + '-' + this.id + '-last-tab-open')
        this.state = {
            selectedTab: this.lastTabOpen || 'about'
        }
    }

    userAndTeacherDataFetched() {
        super.userAndTeacherDataFetched()
        // if there's no teacher with the given id, redirect to creator-edit-teacher page for new teacher profile to be created:        
        if (!this.teacherData) {            
            this.setState({navigateTo: '/creator/edit-teacher'})
        } else if (Database.isTopicsDataFetched()) 
            this.topicsDataFetched()
        else
            Database.database_fetchTopicsData()
    }
    
    // allDataAndCurriculumDataFetched doesn't cover topics data fetched. Make sure it gets done before we render the form:    
    topicsDataFetched() { 
        // if we're navigating from a previously page and the curr id data was already fetched, we need to manually trigger the process of page rendering:
        if (!this.isNew)
            this.curriculumData = Database.getCurriculumData(this.id)       
        if (this.isNew || this.curriculumData)
            this.allDataAndTopicsDataAndCurriculumDataFetched()
    }

    // allDataAndCurriculumDataFetched doesn't cover topics data fetched. Make sure it gets done before we render the form:
    allDataAndCurriculumDataFetched() {           
        if (Database.isTopicsDataFetched())
            this.allDataAndTopicsDataAndCurriculumDataFetched()        
    }

    allDataAndTopicsDataAndCurriculumDataFetched() {        
        if (this.isNew) {
            this.lessonsData = {}
            this.isInDraftStage = true
            this.language = generalConfig.defaultLanguage
            // if this is a new curr, we're ready to render page:
            this.setState({dataFetched: true})
        } else {
            // this is a previously saved curriculum. get its data and validate it
            // if id supplied in the url params and it's not found or the curriculum doesn't belong to the current teacher user, it's an error. navigate to creator home page:
            this.curriculumData = Database.getCurriculumData(this.id)
            if (!this.curriculumData || (this.curriculumData.teacher !== globals.sessionInfo.username)) {
                this.setState({navigateTo: '/creator/library'})
                return
            }
            this.randomKey = this.curriculumData['random-key']
            this.subject = this.curriculumData.subject
            this.topic = this.curriculumData.topic
            this.language = this.curriculumData['the-language']
            let termNameAndYear = this.curriculumData['term-last-taught'].split(',')
            this.termName = termNameAndYear[0]
            this.termYear = termNameAndYear[1]            
            this.isInSubmissions = this.curriculumData['in-submissions']
            this.isInDrafts = this.curriculumData['in-drafts']
            this.isInProductions = !(this.isInSubmissions || this.isInDrafts)
            this.isInDraftStage = this.isInDrafts
            // now fetch the lessons data (if any) before rendering page:
            Database.database_fetchLessonsData(this.id, this.curriculumData.lessons)
        }
    }

    // database api will call this once lessons data for curriculum is fetched:
    lessonsDataFetched() {
        this.lessonsData = this.curriculumData.lessonsData
        // we got all data. ready to render page:
        this.setState({dataFetched: true})
    }

    // sets the subject each time the pull down option changes
    // also sets the curriculum id for new ones now that the subject is known (subject prefix goes in the id):
    setSubject(subject) {
        this.subject = subject
        // generate a new id for the new curriculum here
        if (this.isNew && !this.id) {
            this.id = CreatorUtility.generateNewCurriculumId(this.subject || this.formProposedSubjectInputRef.current.value)
            this.randomKey = CreatorUtility.generateNewCurriculumRandomKey()
        }
    }

    // deletes a list of lessons entries from the database, because the user just deleted them from the course plan, also updates the curriculum lesson listings in the db:
    deleteLessons(lessons) {
        if (lessons.length === 0)
            return
        let isOne = lessons.length === 1 
        let onSuccessFn = () => {
            let afterSaveFn = () => {
                Utility.showTextDialog(0, 'Your lesson' + (isOne ? ' is' : 's are') + ' deleted.', generalConfig.dialogBoxOKIconClassName)
            }                
            this.saveForm(afterSaveFn)
        }
        if (isOne)
            Database.database_deleteLesson(this.id, lessons[0], this.curriculumData, true, onSuccessFn)
        else
            Database.database_deleteLessons(this.id, lessons, this.curriculumData, true, onSuccessFn)
    }
    
    // collects the filled out form into a new object used for submitForm or saveForm functions:
    collectFormData() {
        let title = this.formTitleRef.current.value.trim()
        let subtitle = this.formSubtitleRef.current.value.trim()
        let coursePlanData = this.curriculumFormCoursePlan.current.getList()
        let gradeMinVal = this.formMinGradeRef.current.getValue()
        let gradeMaxVal = this.formMaxGradeRef.current.getValue()
        let priceTierVal = this.formPriceTierRef.current.getValue()
        let numberOfWeeksVal = this.formNumberOfWeeksRef.current.getValue()
        let numberOfLessonsPerWeekVal = this.formNumberOfLessonsPerWeekRef.current.getValue()
        let numberOfMinutesPerLessonVal = this.formNumberOfMinutesPerLessonRef.current.getValue()
        let numberOfTestsVal = this.formNumberOfTestsRef.current.getValue()
        let otherProofsData = this.otherProofsRefs.current.getList()
        let teacherRequirements = this.curriculumFormTeacherRequirements.current.getList()
        let studentRequirements = this.curriculumFormStudentRequirements.current.getList()
        let resourcesData = this.curriculumFormResources.current.getList()
        let promos = this.promosRefs.current.getList()
        let automaticApplyPromos = promos.filter(i => i.isAutomatic)
        let grade = [gradeMinVal == null ? curriculumConfig.defaultGrade : parseInt(gradeMinVal, 10)]
        if (gradeMaxVal != null)
            grade.push(parseInt(gradeMaxVal, 10))        
        let formData = {
            'single-unit': this.formCurriculumTypeRef.current.getValue() === 'true',
            'has-instruction-video': this.formHasInstructionVideoRef.current.getValue() === 'true',
            subject: this.subject || this.formProposedSubjectInputRef.current.value,
            topic: this.topic || this.formProposedTopicInputRef.current.value,
            title: title,
            'title-lc': title.toLowerCase(),
            subtitle: subtitle,
            'subtitle-lc': subtitle.toLowerCase(),
            // Note: using == null to match both null and undefined:
            grade: grade,
            'the-language': this.language || this.formProposedLanguageInputRef.current.value,
            'price-tier': priceTierVal == null ? undefined : parseInt(priceTierVal, 10),
            'learn-what': this.curriculumFormLearnWhat.current.getList(),
            'search-terms': this.curriculumFormSearchTerms.current.getList().map(i => i.toLowerCase()),
            'num-weeks': numberOfWeeksVal == null ? undefined : parseInt(numberOfWeeksVal, 10),
            'num-lessons-per-week': numberOfLessonsPerWeekVal == null ? undefined : parseInt(numberOfLessonsPerWeekVal, 10),
            'num-minutes-per-lesson': numberOfMinutesPerLessonVal == null ? undefined : parseInt(numberOfMinutesPerLessonVal, 10),
            'num-resources': this.numLessonResourcesCounted,
            'num-tests': numberOfTestsVal == null ? undefined : parseInt(numberOfTestsVal, 10),
            'school-taught-title': this.formSchoolNameRef.current.value.trim(),
            'school-taught-website': this.formSchoolWebsiteRef.current.value.trim(),
            termName: this.formTermNameRef.current.getValue(),
            termYear: this.termYear || this.formProposedTermYearInputRef.current.value,
            'teacher-requirements': teacherRequirements.filter(i => !i.isOptional).map(i => i.description),
            'teacher-requirements-optionals': teacherRequirements.filter(i => i.isOptional).map(i => i.description),
            'student-requirements': studentRequirements.filter(i => !i.isOptional).map(i => i.description),
            'student-requirements-optionals': studentRequirements.filter(i => i.isOptional).map(i => i.description),
            descriptionText: this.formDescriptionTextEditor.current.getStylizedTextAsHtml(),
            'section-titles': coursePlanData.map(i => i.sectionTitle),
            'section-lesson-indices': [0].concat((coursePlanData.map(i => i.lessonItems.length).reduce((acc, curr) => [acc[0] + curr, acc[1].concat([acc[0] + curr])], [0, []]))[1]).slice(0, coursePlanData.length),
            lessons: coursePlanData.map(i => i.lessonItems).reduce((acc, curr) => acc.concat(curr.map(c => c.lesson)), []),
            'lesson-titles': coursePlanData.map(i => i.lessonItems).reduce((acc, curr) => acc.concat(curr.map(c => c.lessonTitle)), []),
            sectionLessonCounts: coursePlanData.map(i => i.lessonItems.length),
            'resource-descriptions': resourcesData.map(i => i.description),
            'resources': resourcesData.map(i => i.url.trim()),
            'resource-is-externals': resourcesData.map(i => i.isExternal),
            'resource-is-cloud-storages': resourcesData.map(i => i.isCloudStorage),
            'resource-cloud-storage-not-copieds': resourcesData.map(i => i.cloudStorageNotYetCopied),
            'teacher-notes': this.formTeacherNotes.current.getList(),
            'other-proof-descriptions': otherProofsData.map(i => i.description),
            'other-proofs': otherProofsData.map(i => i.url.trim()),
            'other-proof-is-externals': otherProofsData.map(i => i.isExternal),
            'promo-codes': promos.map(i => i.promo),
            'promo-tiers': promos.map(i => i.tier),
            'promo-code-current': automaticApplyPromos.length === 1 ? automaticApplyPromos[0].promo : ''
        }
        // default section title if creator is using a single section without a title:
        if (formData['section-titles'].length === 1 && !formData['section-titles'][0])
            formData['section-titles'][0] = 'Unit'
        // also compute total num-tests and num-assignments from all lessons:
        // creator user manually gives the number of tests (and we just verify the count matches the lesson's total marked resources), but we automatically count the number of assignments, so here we directly will set the count field 'num-assignments'...:
        formData.numTestsCounted = 0
        formData['num-assignments'] = 0
        let otherResourcesOptionalFields = curriculumConfig.lessonsOtherResourcesOptionalFields
        let lessonsData = this.lessonsData
        for (let lesson of formData.lessons) {
            let lessonData = lessonsData[lesson]
            if (lessonData === undefined)                
                continue
            // keep track of resources in this lesson that are marked as a test/assignment:            
            Object.keys(otherResourcesOptionalFields).forEach(f => {
            let attributeName = otherResourcesOptionalFields[f]
            let otherResourcesIsOptionalThing = lessonData[f]
            if (otherResourcesIsOptionalThing) {
                let addToCount = otherResourcesIsOptionalThing.filter(i => i).length
                formData[attributeName] += addToCount        
            }
            })            
        }
        //console.log(formData)
        return formData
    }

    // if files have been uploaded using upload zones (for curriculum images, proof docs), or description has been updated using text editor field, this updates the cached db fields for the curriculum and sets a flag (return value) that db entry for the curriculum needs to be forced to update
    // fileserverStatus = true --> anything on fs was updated
    // fileserverCleanupNeeded = true --> new files were uploaded, thus old files need to be removed on fs
    processUploadedFiles(curriculumData, formData) {
        let fileserverUpdated = false, fileserverCleanupNeeded = false
        // process all uploaded files except other proofs which is stored in a list:
        Object.keys(this.curriculumFilesUploadedNames).forEach(field => {
            // if files have been uploaded, db also needs to be updated because the file on filesever has changed even if no other db fields have changed (so that an update entry will be made with last-update-date and the lambda triggered to handle the update):
            fileserverUpdated = true
            fileserverCleanupNeeded = true
            curriculumData[field] = this.curriculumFilesUploadedNames[field]
            // remove it so we don't force update unnecessarily next time curr is saved:
            delete this.curriculumFilesUploadedNames[field]
        })
        // process any other proofs which is stored in a list:
        let updates = this.otherProofFilesUploadedNames
        Object.keys(updates).forEach(index => {
            // if files have been uploaded, db also needs to be updated because the file on filesever has changed even if no other db fields have changed (so that an update entry will be made with last-update-date and the lambda triggered to handle the update):
            fileserverUpdated = true
            fileserverCleanupNeeded = true
            curriculumData['other-proofs'][index] = updates[index]                
            // remove it so we don't force update unnecessarily next time curr is saved:
            delete this.otherProofFilesUploadedNames[index]
        })
        // handle uploading the description file on the fs in case this is a new curriculum or an old one whose description was updated:            
        let isDescriptionUpdated = this.isNew || (formData.descriptionText !== this.descriptionText)
        if (isDescriptionUpdated) {
            // also db needs to be updated because the file on filesever has changed even if no other db fields have changed (so that an update entry will be made with last-update-date and the lambda triggered to handle the update):
            fileserverUpdated = true
            let descriptionUploadFileHtmlText = CreatorUtility.generateDescriptionHtmlFileFromHtmlText(this.id, formData.descriptionText)
            Fileserver.uploadCurriculumDescriptionFile(this.id, this.teacher, !this.isNew && this.curriculumData['in-productions'], fsConfig.curriculumDescriptionFile, descriptionUploadFileHtmlText)
            this.descriptionText = formData.descriptionText
        }
        return {fileserverUpdated: fileserverUpdated, fileserverCleanupNeeded: fileserverCleanupNeeded}
    }

    // creates function that will be called once an other proofs file is dropped in the other proofs drop zone:
    generateResourceOnDropFn(isSlides, isResource, index, uploadZoneRef, urlInputElementRef) {
        let fn = (file, fileName, origFileName) => {
            let onUploadSuccessFn = () => {                
                this.otherProofFilesUploadedNames[index] = fileName
                urlInputElementRef.current.value = fileName
                uploadZoneRef.current.onUploadSuccessFn(origFileName)
            }
            let onUploadFailureFn = () => {
                uploadZoneRef.current.onUploadFailureFn()
            }            
            Fileserver.uploadCurriculumResourceOrProofFile(isResource, this.randomKey, this.id, this.teacher, !this.isNew && this.curriculumData['in-productions'], fileName, file, onUploadSuccessFn, onUploadFailureFn)
        }
        return fn.bind(this)
    }

    // saves a new curriculum draft on the database for later retrival
    saveForm(afterSaveFn, isDueToLessonEditButtonClicked = false) {
        let error = false
        let formData = this.collectFormData()
        let err = CreatorUtility.validateCurriculumForm(false, this, formData, isDueToLessonEditButtonClicked)
        if (err) {
            Utility.showTextDialog(0, err, generalConfig.dialogBoxErrorIconClassName)
            error = err
        } else {
            let onFailureFn = () => {
                let onCloseFn = () => {
                    if (afterSaveFn !== undefined)
                        afterSaveFn()
                }
                if (afterSaveFn === undefined)
                    Utility.showTextDialog(0, 'There was a problem saving the curriculum draft!', generalConfig.dialogBoxErrorIconClassName, onCloseFn)
                else
                    onCloseFn()
            }
            let onSuccessFn = () => {
                let onCloseFn = () => {
                    let wasNew = this.isNew
                    this.isNew = false
                    this.curriculumData = Database.getCurriculumData(this.id)
                    this.lastSavedFormData = formData
                    if (afterSaveFn !== undefined)
                        afterSaveFn()
                    else if (wasNew) {
                        this.isInDrafts = true
                        this.isInDraftStage = true
                        // force a rerender so that CTA buttons for existing draft show up:
                        this.topCTAComponentRef.current.setState({dataFetched: true})
                    }
                }
                if (afterSaveFn === undefined)
                    Utility.showTextDialog(0, 'Your curriculum draft is saved.', generalConfig.dialogBoxOKIconClassName, onCloseFn)
                else
                    onCloseFn()
            }
            // if grade range is only one grade, represent it as a singleton list instead of a [min,max] list:
            if (formData.grade != null && formData.grade.length > 1 && formData.grade[0] == formData.grade[1])
                formData.grade.pop()
            let isNew = this.isNew
            let topicTitle = Database.getTopicTitle(formData.topic)            
            let curriculumData = {'random-key': this.randomKey, 'single-unit': formData['single-unit'], subject: formData.subject, topic: formData.topic, 'topic-title': topicTitle, 'topic-title-lc': topicTitle.toLowerCase(), title: formData.title, 'title-lc': formData['title-lc'], subtitle: formData.subtitle, 'subtitle-lc': formData['subtitle-lc'], grade: formData.grade, 'price-tier': formData['price-tier'] === undefined ? curriculumConfig.defaultPriceTier : formData['price-tier'], 'learn-what': formData['learn-what'], 'search-terms': formData['search-terms'], 'num-weeks': formData['num-weeks'] || curriculumConfig.defaultNumberOfWeeks, 'num-lessons-per-week': formData['num-lessons-per-week'] || curriculumConfig.defaultNumberOfLessonsPerWeek, 'num-minutes-per-lesson': formData['num-minutes-per-lesson'] || curriculumConfig.defaultLessonDurationsMinutes, 'num-resources': formData['num-resources'] || 0, 'num-tests': formData['num-tests'] === undefined ? curriculumConfig.defaultNumberOfTests : formData['num-tests'], 'num-assignments': formData['num-assignments'] === undefined ? curriculumConfig.defaultNumberOfAssignments : formData['num-assignments'], 'has-instruction-video': formData['has-instruction-video'], 'school-taught-title': formData['school-taught-title'] || '', 'school-taught-website': formData['school-taught-website'] || '', 'term-last-taught': (formData.termName || '') + ',' + (formData.termYear || ''), 'teacher-requirements': formData['teacher-requirements'], 'teacher-requirements-optionals': formData['teacher-requirements-optionals'], 'student-requirements': formData['student-requirements'], 'student-requirements-optionals': formData['student-requirements-optionals'], 'section-titles': formData['section-titles'], 'lessons': formData.lessons, 'lesson-titles': formData['lesson-titles'], 'section-lesson-indices': formData['section-lesson-indices'], image: isNew ? '' : (this.curriculumData.image || ''), previews: isNew ? '' : (this.curriculumData.previews || ''), 'teacher-notes': formData['teacher-notes'], 'school-proof': isNew ? '' : (this.curriculumData['school-proof'] || ''), 'teaching-proof': isNew ? '' : (this.curriculumData['teaching-proof'] || ''), 'other-proof-descriptions': formData['other-proof-descriptions'], 'other-proofs': formData['other-proofs'], 'other-proof-is-externals': formData['other-proof-is-externals'], 'resource-descriptions': formData['resource-descriptions'], 'resources': formData['resources'], 'resource-is-externals': formData['resource-is-externals'], 'promo-codes': formData['promo-codes'], 'promo-tiers': formData['promo-tiers'], 'promo-code-current': formData['promo-code-current'] || ''}
            // only add language field if not default:
            let formLanguage = formData['the-language']
            if (isNew ?
                    (formLanguage !== generalConfig.defaultLanguage) :
                    (formLanguage !== this.curriculumData['the-language'])) {
                curriculumData['the-language'] = formLanguage === generalConfig.defaultLanguage ? '' : formLanguage
            }
            let fileserverStatus = this.processUploadedFiles(curriculumData, formData)
            // Save the new curriculum draft or update draft:
            Utility.showWaitingDialog()
            if (isNew)
                Database.database_addNewCurriculumDraft(this.id, curriculumData, this.teacherData, onSuccessFn, onFailureFn)
            else
                Database.database_updateCurriculum(this.id, curriculumData, fileserverStatus.fileserverUpdated, fileserverStatus.fileserverCleanupNeeded, onSuccessFn, onFailureFn)
        }
        return error
    }

    // submits a new curriculum or edits to a submitted/live one:
    submitForm(afterSaveFn) {
        let formData = this.collectFormData()
        let err = CreatorUtility.validateCurriculumForm(true, this, formData)        
        if (err) {
            Utility.showTextDialog(0, err, generalConfig.dialogBoxErrorIconClassName)
        } else {
            let onFailureFn = () => {
                let onCloseFn = () => {                    
                    if (afterSaveFn !== undefined)
                        afterSaveFn()                   
                }
                if (afterSaveFn === undefined)
                    Utility.showTextDialog(0, 'There was a problem submitting the curriculum data!', generalConfig.dialogBoxErrorIconClassName, onCloseFn)
                else
                    onCloseFn()
            }
            let onSuccessFn = () => {
                let onCloseFn = () => {
                    this.lastSavedFormData = formData
                    this.curriculumData['the-language'] = formData['the-language']
                    if (afterSaveFn !== undefined)
                        afterSaveFn()
                    else
                        this.setState({navigateTo: '/creator/library'})
                }
                if (afterSaveFn === undefined)
                    Utility.showTextDialog(0, this.isInDraftStage ? 'Thank you for submitting your curriculum!\nIt will be reviewed soon...' : 'Your curriculum update is submitted.', generalConfig.dialogBoxOKIconClassName, onCloseFn)
                else
                    onCloseFn()
            }
            // if grade range is only one grade, represent it as a singleton list instead of a [min,max] list:
            if (formData.grade != null && formData.grade.length > 1 && formData.grade[0] == formData.grade[1])
                formData.grade.pop()
            let isNew = this.isNew
            let topicTitle = Database.getTopicTitle(formData.topic)
            let curriculumData = {'random-key': this.randomKey, topic: formData.topic, 'topic-title': topicTitle, 'topic-title-lc': topicTitle.toLowerCase(), grade: formData.grade, 'term-last-taught': formData.termName + ',' + formData.termYear, image: isNew ? '' : (this.curriculumData.image || ''), previews: isNew ? '' : (this.curriculumData.previews || ''), 'school-proof': isNew ? '' : (this.curriculumData['school-proof'] || ''), 'teaching-proof': isNew ? '' : (this.curriculumData['teaching-proof'] || '')}
            let fields = ['single-unit', 'subject', 'title', 'title-lc', 'subtitle', 'subtitle-lc', 'price-tier', 'learn-what', 'search-terms', 'num-weeks', 'num-lessons-per-week', 'num-minutes-per-lesson', 'num-resources', 'num-tests', 'num-assignments', 'has-instruction-video', 'school-taught-title', 'school-taught-website', 'teacher-requirements', 'teacher-requirements-optionals', 'student-requirements', 'student-requirements-optionals', 'section-titles', 'lessons', 'lesson-titles', 'section-lesson-indices', 'teacher-notes',  'other-proof-descriptions', 'other-proofs', 'other-proof-is-externals', 'resource-descriptions', 'resources', 'resource-is-externals', 'promo-codes', 'promo-tiers', 'promo-code-current']
            fields.forEach(f => { curriculumData[f] = formData[f] })
            // only add language field if not default:
            let formLanguage = formData['the-language']
            if (isNew ? 
                    (formLanguage !== generalConfig.defaultLanguage) :
                    (this.isInDraftStage || (formLanguage !== this.curriculumData['the-language']))) {
                curriculumData['the-language'] = formLanguage === generalConfig.defaultLanguage ? '' : formLanguage
            }
            let fileserverStatus = this.processUploadedFiles(curriculumData, formData)            
            // If any proposals for new subject/topics/languages also submit it beforehand:
            let isProposedSubject = !Database.isSubjectInDatabase(this.subject)
            let isProposedTopic = isProposedSubject || !Database.isSubjectTopicInDatabase(this.subject, this.topic)
            let isProposedLanguage = !Database.isLanguageInDatabase(formLanguage)
            if (isProposedSubject || isProposedTopic || isProposedLanguage) {
                let curriculumDataParamForProposal = isNew ? {'in-productions': false, title: formData.title} : this.curriculumData
                if (isProposedSubject)
                    Database.database_addNewSubjectProposal(this.id, formData.subject, curriculumDataParamForProposal)
                if (isProposedTopic)
                    Database.database_addNewTopicProposal(this.id, formData.subject, formData.topic, curriculumDataParamForProposal)
                if (isProposedLanguage)
                    Database.database_addNewLanguageProposal(this.id, formData.subject, formLanguage, curriculumDataParamForProposal)
            }
            // Submit the new curriculum or update:
            Utility.showWaitingDialog()
            if (this.isInDraftStage)
                Database.database_addNewCurriculumSubmission(this.id, curriculumData, this.teacherData, onSuccessFn, onFailureFn)
            else
                Database.database_updateCurriculum(this.id, curriculumData, fileserverStatus.fileserverUpdated, fileserverStatus.fileserverCleanupNeeded, onSuccessFn, onFailureFn)
        }
    }

    // dialog for toggling a live curriculum active/inactive ("remove from store")
    toggleCurriculumActive() {
        let teacher = this.teacher
        let teacherData = this.teacherData
        let isActive = this.curriculumData.active
        CreatorUtility.showButtonDialog(this.id, this.curriculumData, (isActive ? 'This will remove your curriculum from the store. Users who have already purchased it will continue to have access to it. Other users will not be able to search for or purchase it anymore. You can put it back in the store at any time.' : 'This will put the curriculum back in the store. Users will be able to find and purchase it.') + '\nAre you sure?', isActive ? 'Remove' : 'Put back', (curriculum, curriculumData) => CreatorUtility.toggleCurriculumActive(curriculum, curriculumData, teacher, teacherData, () => { this.setState({navigateTo: '/creator/library'}) }), false)
    }

    // dialog for deleting a curriculum that is in drafts:
    deleteCurriculum() {
        let teacher = this.teacher
        let teacherData = this.teacherData
        CreatorUtility.showButtonDialog(this.id, this.curriculumData, 'This will permanently delete your curriculum.\nAre you sure?', 'Delete', (curriculum, curriculumData) => CreatorUtility.deleteCurriculum(curriculum, curriculumData, teacher, teacherData, () => { this.setState({navigateTo: '/creator/library'}) }), true)
    }

    // dialog for removing a submitted curriculum out of store and moving it into drafts:
    removeCurriculumFromStore() {
        let teacher = this.teacher
        let teacherData = this.teacherData
        CreatorUtility.showButtonDialog(this.id, this.curriculumData, 'This will remove your curriculum submission, but keeps it in your drafts.\nAre you sure?', 'Remove', (curriculum, curriculumData) => CreatorUtility.removeCurriculumFromStore(curriculum, curriculumData, teacher, teacherData, () => { this.setState({navigateTo: '/creator/library'}) }), true)
    }

    // checks if subject is chosen already. If so returns false, If not so, it prompts the user and returns error flag=true:
    checkForSubjectShouldBeChosenFirstError(onUploadFailureFn) {
        if (!(this.subject || this.formProposedSubjectInputRef.current.value)) {
            Utility.showTextDialog(0, 'Choose a subject first.', generalConfig.dialogBoxErrorIconClassName)
            onUploadFailureFn()
            return true
        }
        return false
    }

    // when curriculum image file is dropped in upload zone:
    curriculumImageOnDropFn(file, fileName, origFileName) {        
        let onUploadFailureFn = () => {
            this.imageUploadZoneRef.current.onUploadFailureFn()
        }
        if (this.checkForSubjectShouldBeChosenFirstError(onUploadFailureFn))
            return
        let onUploadSuccessFn = () => {
            this.imageUploaded = true
            this.curriculumFilesUploadedNames['image'] = fileName
            let url = Fileserver.getCurriculumImageUrl(this.id, this.teacher, !this.isNew && this.curriculumData['in-productions'], true, fileName)
            this.imageUploadZoneRef.current.onUploadSuccessFn(url)
        }
        Fileserver.uploadCurriculumImageFile(this.id, this.teacher, !this.isNew && this.curriculumData['in-productions'], fileName, file, onUploadSuccessFn, onUploadFailureFn)        
    }

    // when curriculum previews file is dropped in upload zone:
    curriculumPreviewsOnDropFn(file, fileName, origFileName) {
        // if it's a ZIP make sure it has the right extension for identification as such:        
        if ((file.type === 'application/zip') && !fileName.toLowerCase().endsWith('.zip'))
            fileName = fileName + '.zip'
        let onUploadFailureFn = () => {
            this.previewsUploadZoneRef.current.onUploadFailureFn()
        }
        if (this.checkForSubjectShouldBeChosenFirstError(onUploadFailureFn))
            return
        let onUploadSuccessFn = () => {
            this.previewsUploaded = true
            this.curriculumFilesUploadedNames['previews'] = fileName
            this.previewsUploadZoneRef.current.onUploadSuccessFn(origFileName)
        }
        Fileserver.uploadCurriculumPreviewsFile(this.id, this.teacher, !this.isNew && this.curriculumData['in-productions'], fileName, file, onUploadSuccessFn, onUploadFailureFn)
    }
   
    generateCurriculumProofOnDropFn(isSchoolProof) {
        let fn = (file, fileName, origFileName) => {
            let uploadDropZoneRef = isSchoolProof ? this.schoolProofUploadZoneRef : this.teachingProofUploadZoneRef
            let onUploadSuccessFn = () => {
                this.curriculumProofUploaded[isSchoolProof ? 'school' : 'teaching'] = true
                this.curriculumFilesUploadedNames[isSchoolProof ? 'school-proof' : 'teaching-proof'] = fileName            
                uploadDropZoneRef.current.onUploadSuccessFn(origFileName)
            }
            let onUploadFailureFn = () => {
                uploadDropZoneRef.current.onUploadFailureFn()
            }
            if (this.checkForSubjectShouldBeChosenFirstError(onUploadFailureFn))
                return
            Fileserver.uploadCurriculumResourceOrProofFile(false, this.randomKey, this.id, this.teacher, !this.isNew && this.curriculumData['in-productions'], fileName, file, onUploadSuccessFn, onUploadFailureFn)
        }
        return fn.bind(this)
    }

    termYearOnChangeFn(e, val) {
        if (val) {
            let year = val
            let isPropose = year === 'propose'            
            if (isPropose) {   
                delete this.termYear             
                this.formProposedTermYearInputRef.current.className = 'item-right-of-item'
            } else {
                this.termYear = val
                this.formProposedTermYearInputRef.current.className = 'hidden ' + this.formProposedTermYearInputRef.current.className       
            }
        }
    }

    // called once a lesson copies itself to clipboard:
    copyLessonToClipboard(lesson) {
        this.lessonCopiedToClipboard = lesson
        let coursePlanComponent = this.curriculumFormCoursePlan.current
        // trigger a refresh so the lesson action buttons update:        
        coursePlanComponent.forceUpdate()
        Utility.showTextDialog(0, 'Lesson copied to clipboard.\nTo paste a copy, add a new lesson and then select "Paste lesson" from its menu options.', generalConfig.dialogBoxOKIconClassName)
    }

    // paste copied lesson from clipboard onto a new lesson entry
    async pasteLessonFromClipboard(lessonInputComponent, copyResourcesToo) {
        let fromLesson = this.lessonCopiedToClipboard.lesson
        let toLesson = lessonInputComponent.lesson
        let lessonData = this.curriculumData.lessonsData[fromLesson]
        let toLessonData = {...lessonData}
        Utility.showWaitingDialog()
        let onPasteFailureFn = () => {
            Utility.showTextDialog(0, 'There was an error pasting the lesson!', generalConfig.dialogBoxErrorIconClassName)
        }
        let onPasteSuccessFn = () => {
            this.curriculumData.lessonsData[toLesson] = toLessonData
            lessonInputComponent.onPasteSuccess(this.lessonCopiedToClipboard.lessonTitle)
            // trigger a refresh so the lesson action buttons update:        
            lessonInputComponent.forceUpdate()
            Utility.showTextDialog(0, 'Lesson copy is pasted. You can now edit this copy.\nRemember to save your curriculum!', generalConfig.dialogBoxOKIconClassName)
        }        
        let onFSCopySuccessFn = () => {
            // 2. duplicate lesson DB entry:
            Database.database_addLessonDuplicate(this.id, fromLesson, toLesson, this.curriculumData, onPasteSuccessFn, onPasteFailureFn)    
        }
        // 1. duplicate lesson folder in fileserver. if this is a live curriculum, duplicates will go to the updates folder in fileserver and the lambda function will handle moving it to live...:
        Fileserver.duplicateLessonFiles(this.curriculumData['random-key'], this.id, fromLesson, toLesson, this.teacher, toLessonData, this.curriculumData['in-productions'], copyResourcesToo, onFSCopySuccessFn, onPasteFailureFn)
        
    }

    // auto save or warn user about saving form before leaving:
    promptForSaveBeforeNavigatingAway(link) {
        let navigateToPage = () => {       
            this.setState({navigateTo: link})
        }
        // if form was never saved or the current form has changes over the last saved form warn the user about leaving first:        
        if (this.lastSavedFormData === undefined || (JSON.stringify(this.collectFormData()) !== JSON.stringify(this.lastSavedFormData))) {
            let error = this.saveForm(navigateToPage)
            // if there was an error saving the form:
            if (error)
                Utility.showTextDialog(0, 'Failed to save curriculum due to errors in the form:\n"' + error + '"\nIf you think you might have unsaved changes, click "Cancel", correct the errors in the curriculum form, and save before leaving the page.', generalConfig.dialogBoxErrorIconClassName, undefined, ['Leave anyway', 'Cancel'], [navigateToPage, undefined])
        } else
            navigateToPage()
    }

    renderForm() {
        return (
          <Tabs ref={this.formTabsComponent} id="creator-edit-body-content" className="form-container form-container-extra-top-margin page-body-listing-content section"
            defaultTab={this.state.selectedTab}
            onChange={(tabId) => { if (globals.haveLocalStorage) localStorage.setItem('termeric-page-' + this.pageId + '-' + this.id + '-last-tab-open', tabId) }} >
            <TabList className="many-tabs-list">
              <Tab tabFor='about'><span className="many-tabs-header-curriculum-edit tab-header">About</span></Tab>
              <Tab tabFor='info'><span className="many-tabs-header-curriculum-edit tab-header">Info</span></Tab>
              <Tab tabFor='description'><span className="many-tabs-header-curriculum-edit tab-header">Description</span></Tab>
              <Tab tabFor='plan'><span className="many-tabs-header-curriculum-edit tab-header">Course Plan</span></Tab>
              <Tab tabFor='resources'><span className="many-tabs-header-curriculum-edit tab-header">Resources</span></Tab>
              <Tab tabFor='teacher-notes'><span className="many-tabs-header-curriculum-edit tab-header">Teacher Notes</span></Tab>
              <Tab tabFor='previews'><span className="many-tabs-header-curriculum-edit tab-header">Preview Screenshots</span></Tab>
              <Tab tabFor='proof'><span className="many-tabs-header-curriculum-edit tab-header">Proof of Teaching</span></Tab>
              <Tab tabFor='promos'><span className="many-tabs-header-curriculum-edit tab-header">Promos</span></Tab>
            </TabList>
            <TabPanel tabId='about'>
              {this.renderFormAbout()}
            </TabPanel>
            <TabPanel tabId='info'>
              {this.renderFormInfo()}
            </TabPanel>
            <TabPanel tabId='description'>
              {this.renderFormDescription()}
            </TabPanel>
            <TabPanel tabId='plan'>
              {this.renderFormCoursePlan()}          
            </TabPanel>
            <TabPanel tabId='resources'>                
              {this.renderFormResources()}
            </TabPanel>
            <TabPanel tabId='teacher-notes'>                
              {this.renderFormTeacherNotes()}
            </TabPanel>
            <TabPanel tabId='previews'>
              {this.renderFormPreviews()}
            </TabPanel>
            <TabPanel tabId='proof'>
              {this.renderFormProof()}
            </TabPanel>
            <TabPanel tabId='promos'>
              {this.renderFormPromos()}
            </TabPanel>
          </Tabs>
        )
    }

    renderFormAbout() {
        let isNew = this.isNew
        let curriculumData = this.curriculumData
        let subject = isNew ? undefined : curriculumData.subject
        return (
            <div id="creator-edit-form-about" className="form flex-col">
                <div className="form-item flex-col">
                    <label>Curriculum or Curriculum Unit</label>
                    <span className="form-input-description"><em className="opac">If this is a stand-alone curriculum unit only rather than a full curriculum, select <span className="boxed-text span-with-side-neighbors">Curriculum Unit</span>.</em></span>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Curriculum' : (curriculumData['single-unit'] ? 'Curriculum Unit' : 'Curriculum')} id={'curriculum-form-curriculum-type'} containerExtraClasses={'form-select-div'} options={[false, true]} optionKeyToTitleFn={(idx, p) => { return p ? 'Curriculum Unit' : 'Curriculum' }} onChangeFn={(e) => {}} defaultValue={isNew ? false : curriculumData['single-unit']} addDefault={false} ref={this.formCurriculumTypeRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>Subject</label>
                    <span className="form-input-description"><em className="opac">If you don't see your subject, select <span className="boxed-text span-with-side-neighbors">Propose new subject</span>, and it will get added.</em></span>
                    <div className="flex-row">
                        <SubjectOptions page={this} defaultValue={isNew ? undefined : subject} label={isNew ? undefined : Database.getSubjectTitle(subject)} disabled={this.isInProductions === true} />
                        <input id="curriculum-form-propose-subject" className="hidden item-right-of-item" type="text" placeholder="Name your proposed subject" ref={this.formProposedSubjectInputRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>Topic</label>
                    <span className="form-input-description"><em className="opac">If you don't see your topic, select <span className="boxed-text span-with-side-neighbors">Propose new topic</span>, and it will  get added.</em></span>
                    <div className="flex-row">
                        <TopicOptions page={this} defaultValue={isNew ? undefined : curriculumData.topic} label={isNew ? undefined : Database.getTopicTitle(curriculumData.topic)} topics={isNew ? undefined : Database.getSubjectTopics(subject)} ref={this.topicSelectComponentRef} />
                        <input id="curriculum-form-propose-topic" className="hidden item-right-of-item" type="text" placeholder="Name your proposed topic" ref={this.formProposedTopicInputRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>Course Title</label>
                    <input id="curriculum-form-title" type="text" placeholder="Enter your curriculum's title" defaultValue={isNew ? undefined : curriculumData.title} ref={this.formTitleRef} />
                </div>
                <div className="form-item flex-col">
                    <label>Course Subtitle</label>
                    <span className="form-input-description"><em className="opac">Subtitle is optional.</em></span>
                    <input id="curriculum-form-subtitle" type="text" placeholder="Enter a subtitle for your curriculum" defaultValue={isNew ? undefined : curriculumData.subtitle} ref={this.formSubtitleRef} />
                </div>
                <div className="form-item flex-col">
                    <label>Search Terms</label>
                    <span className="form-input-description"><em className="opac">Add up to 5 additional search terms (other than the name of the subject and topic) that users may search for in order to find this curriculum.</em></span>
                    <span className="form-input-description"><em className="opac">For example: <span className="boxed-text span-with-side-neighbors">cook</span><span className="boxed-text span-with-side-neighbors">recipes</span><span className="boxed-text span-with-side-neighbors">healthy living</span><span className="boxed-text span-with-side-neighbors">vegan</span>.</em></span>
                    <CreatorUtility.TextInputList ref={this.curriculumFormSearchTerms} items={isNew ? undefined : curriculumData['search-terms'].slice()} addMessage="Add a search term" sortable={true} page={this} />
                </div>
                <div className="form-item flex-col">
                    <label>Grade</label>
                    <span className="form-input-description"><em className="opac">Please choose the specific grade range for whom you taught this course. If you taught this course to a specific grade, set both the minimum and maximum grades to the same value.</em></span>
                    <h6>Minimum Grade</h6>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Choose a minimum grade' : (curriculumData['min-grade'] === 0 ? 'Pre/K' : ('Grade ' + curriculumData['min-grade']))} id={'curriculum-form-grade'} containerExtraClasses={'form-select-div'} options={[...Array(13).keys()]} optionKeyToTitleFn={(idx, p) => { return idx === 0 ? 'Pre/K' : ('Grade ' + idx)}} onChangeFn={(e) => {}} defaultValue={isNew ? undefined : curriculumData['min-grade']} addDefault={false} ref={this.formMinGradeRef} />
                    </div>
                    <h6>Maximum Grade</h6>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Choose a maximum grade' : (curriculumData['max-grade'] === 0 ? 'Pre/K' : ('Grade ' + curriculumData['max-grade']))} id={'curriculum-form-grade'} containerExtraClasses={'form-select-div'} options={[...Array(13).keys()]} optionKeyToTitleFn={(idx, p) => { return idx === 0 ? 'Pre/K' : ('Grade ' + idx)}} onChangeFn={(e) => {}} defaultValue={isNew ? undefined : curriculumData['max-grade']} addDefault={false} ref={this.formMaxGradeRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>Language</label>
                    <span className="form-input-description"><em className="opac">If you don't see your language, select <span className="boxed-text span-with-side-neighbors">Add another language</span>.</em></span>
                    <div className="flex-row">
                        <LanguageOptions label={Database.getLanguageTitle(isNew ? generalConfig.defaultLanguage : curriculumData['the-language'])} id={'curriculum-form-language'} containerExtraClasses={'form-select-div'} options={[...Database.getLanguages(), 'propose']} optionKeyToTitleFn={(idx, p) => { return p === 'propose' ? 'Add another language' : Database.getLanguageTitle(p) }} defaultValue={isNew ? generalConfig.defaultLanguage : curriculumData['the-language']} addDefault={false} page={this} ref={this.formLanguageRef} />
                        <input id="curriculum-form-propose-language" className="hidden item-right-of-item" type="text" placeholder="Name the language" ref={this.formProposedLanguageInputRef} />
                    </div>
                </div>
                <div className="form-item flex-col">                    
                    <label>Course Image</label>
                    <span className="form-input-description"><em className="opac">Use an image with 16:9 aspect ratio.</em></span>
                    <CreatorUtility.FileUploadDropZone accept={'image/*'} hasImagePreview={true} extraClasses={'curriculum-dropzone'} isNew={isNew} previewImagePlaceHolder={generalConfig.curriculumImagePlaceHolder} previewFile={isNew ? undefined : curriculumData.imageUrl} newMessage={this.uploadDropZoneNewMessage.replace('@', 'an image (aspect ratio 16:9)')} editMessage={this.uploadDropZoneEditMessage.replace('@', 'an image (aspect ratio 16:9)')} onSuccessfulDropFn={this.curriculumImageOnDropFn.bind(this)} ref={this.imageUploadZoneRef} />
                </div>                
            </div>
        )
    }

    renderFormInfo() {
        let isNew = this.isNew
        let curriculumData = this.curriculumData
        let priceTierPricesLocal = payConfig.priceTierPrices.local
        return (
            <div id="creator-edit-form-about" className="form flex-col">
                <div className="form-item flex-col">
                    <label>Learning Outcomes</label>
                    <span className="form-input-description"><em className="opac">List the main goals for this course--upon completing it, what will the students learn.</em></span>
                    <CreatorUtility.TextInputList ref={this.curriculumFormLearnWhat} items={isNew ? undefined : curriculumData['learn-what'].slice()} sortable={true} page={this} />
                </div>
                <div className="form-item flex-col">
                    <label>Number of Lessons per Week</label>  
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Choose number of lessons per week' : curriculumData['num-lessons-per-week']} id={'curriculum-form-num-lessons-per-week'} containerExtraClasses={'form-select-div'} options={[...Array(curriculumConfig.maxNumberOfLessonsPerWeek + 1).keys()].slice(1)} optionKeyToTitleFn={(idx, p) => { return p }} onChangeFn={(e) => {}} defaultValue={isNew ? undefined : curriculumData['num-lessons-per-week']} addDefault={false} ref={this.formNumberOfLessonsPerWeekRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>Lesson Duration</label>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Choose duration of lessons' : curriculumData['num-minutes-per-lesson'] + ' min.'} id={'curriculum-form-lesson-duration'} containerExtraClasses={'form-select-div'} options={curriculumConfig.allowedLessonDurationsMinutes} optionKeyToTitleFn={(idx, p) => { return p + ' min.' }} onChangeFn={(e) => {}} defaultValue={isNew ? undefined : curriculumData['num-minutes-per-lesson']} addDefault={false} ref={this.formNumberOfMinutesPerLessonRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>Number of Weeks</label>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Choose number of weeks' :  curriculumData['num-weeks']} id={'curriculum-form-num-weeks'} containerExtraClasses={'form-select-div'} options={Utility.arrayFromTo(1, curriculumConfig.maxNumberOfWeeks)} optionKeyToTitleFn={(idx, p) => { return p }} onChangeFn={(e) => {}} defaultValue={isNew ? undefined : curriculumData['num-weeks']} addDefault={false} ref={this.formNumberOfWeeksRef} />
                    </div>
                </div>                
                <div className="form-item flex-col">    
                    <label>Number of (Midterm/Final) Tests</label>
                    <span className="form-input-description"><em className="opac">Only major tests that have a considerable percentage in the final grade. Do not count other casual tests and quizzes that you might give as part of some of the lessons.</em></span>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Choose number of tests' :  curriculumData['num-tests']} id={'curriculum-form-num-weeks'} containerExtraClasses={'form-select-div'} options={Utility.arrayFromTo(0, curriculumConfig.maxNumberOfTests)} optionKeyToTitleFn={(idx, p) => { return p }} onChangeFn={(e) => {}} defaultValue={isNew ? undefined : curriculumData['num-tests']} addDefault={false} ref={this.formNumberOfTestsRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>Do Lessons Contain Instruction Video?</label>
                    <span className="form-input-description"><em className="opac">Select <span className="boxed-text span-with-side-neighbors">Yes</span> if you are including a link to the <strong>instruction video</strong> with you or your voice-over giving instruction of the lesson under each lesson's Resources section.</em></span>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={(isNew || !curriculumData['has-instruction-video']) ? 'No' : 'Yes'} id={'curriculum-form-curriculum-has-instruction-video'} containerExtraClasses={'form-select-div'} options={[false, true]} optionKeyToTitleFn={(idx, p) => { return p ? 'Yes' : 'No' }} onChangeFn={(e) => {}} defaultValue={isNew ? false : curriculumData['has-instruction-video']} addDefault={false} ref={this.formHasInstructionVideoRef} />
                    </div>
                </div>
                <div className="form-item flex-col">  
                    <label>Suggest a Price</label>
                    <span className="form-input-description"><em className="opac">Price and price tier of your curriculum might be adjusted based on market valuation.</em></span>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Choose a price tier' : ('Tier ' + (curriculumData['price-tier']) + ': ' + Utility.priceToString(priceTierPricesLocal[curriculumData['price-tier']]))} id={'curriculum-form-price-tier'} containerExtraClasses={'form-select-div'} options={payConfig.priceTiers} optionKeyToTitleFn={(idx, p) => { return 'Tier ' + (idx) + ': ' + Utility.priceToString(priceTierPricesLocal[p]) }} onChangeFn={(e) => {}} defaultValue={isNew ? undefined : curriculumData['price-tier']} addDefault={false} ref={this.formPriceTierRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>School Last Taught</label>
                    <input id="curriculum-form-school-name" type="text" placeholder="Enter the name of the school where you last taught this course" defaultValue={isNew ? undefined : curriculumData['school-taught-title']} ref={this.formSchoolNameRef} />
                </div>
                <div className="form-item flex-col">
                    <label>School Website</label>
                    <input id="curriculum-form-school-website" type="text" placeholder="Enter your school's website" defaultValue={isNew ? undefined : curriculumData['school-taught-website']} ref={this.formSchoolWebsiteRef} />
                </div>
                <div className="form-item flex-col">
                    <label>Term Last Taught</label>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Choose a term' : this.termName} id={'curriculum-form-term-name'} containerExtraClasses={'form-select-div'} options={creatorConfig.termNames} optionKeyToTitleFn={(idx, p) => { return p }} onChangeFn={(e) => {}} defaultValue={isNew ? undefined : this.termName} addDefault={false} ref={this.formTermNameRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>Term Last Taught: Year</label>
                    <div className="flex-row flex-center">
                        <Utility.OptionsSelect label={isNew ? 'Choose a year' : this.termYear} id={'curriculum-form-term-year'} containerExtraClasses={'form-select-div'} options={[...CreatorUtility.getPastNYears(10), 'propose']} optionKeyToTitleFn={(idx, p) => { return p === 'propose' ? 'Enter an earlier year' : p }} onChangeFn={this.termYearOnChangeFn.bind(this)} defaultValue={isNew ? undefined : this.termYear} addDefault={false} />    
                        <input id="curriculum-form-propose-term-year" className="hidden item-right-of-item" type="text" placeholder="Enter the year you taught the course" ref={this.formProposedTermYearInputRef} />
                    </div>
                </div>
                <div className="form-item flex-col">
                    <label>Requirements for Teacher</label>
                    <h6>Software/Hardware items the teacher needs...</h6>
                    <span className="form-input-description"><em className="opac">If you want to include a link (URL), use the format:&nbsp;<span className="monospace"><small>[[Title||URL]]</small></span></em></span>
                    <CreatorUtility.RequirementInputList ref={this.curriculumFormTeacherRequirements} items={isNew ? undefined : [...curriculumData['teacher-requirements'].map(i => { return {description: i, isOptional: false }}), ...curriculumData['teacher-requirements-optionals'].map(i => { return {description: i, isOptional: true }})]} isTeacherRquirement={true} sortable={true} page={this} />
                </div>
                <div className="form-item flex-col">
                    <label>Requirements for Students</label>
                    <h6>Software/Hardware items the students need...</h6>
                    <span className="form-input-description"><em className="opac">If you want to include a link (URL), use the format:&nbsp;<span className="monospace"><small>[[Title||URL]]</small></span></em></span>                 
                    <CreatorUtility.RequirementInputList ref={this.curriculumFormStudentRequirements} items={isNew ? undefined : [...curriculumData['student-requirements'].map(i => { return {description: i, isOptional: false }}), ...curriculumData['student-requirements-optionals'].map(i => { return {description: i, isOptional: true }})]} isTeacherRquirement={false} sortable={true} page={this} />
                </div>
            </div>
        )
    }

    renderFormDescription() {
        return (
            <div id="creator-edit-form-description" className="form flex-col">    
                <div className="form-item flex-col">        
                    <label>Description</label>
                    <span className="form-input-description"><em className="opac">To see some examples of good course descriptions, view a few courses on <a className="resource-link" href="https://www.udemy.com/"  target="_blank">Udemy</a> website.</em></span>
                    <CreatorUtility.EditorWithHtmlImportExport id="curriculum-form-description" ref={this.formDescriptionTextEditor} placeholder="Write a complete description of your curriculum..." defaultContentHtml={undefined} />
                </div>
            </div>
        )
    }

    renderFormCoursePlan() {
        let isNew = this.isNew
        let curriculumData = this.curriculumData
        let items = [], sectionTitles, numResourcesCounted = 0
        if (!isNew) {
            sectionTitles = curriculumData['section-titles']
            let lessons = curriculumData['lessons']
            let lessonTitles = curriculumData['lesson-titles']
            let sectionLessonIdxs = curriculumData['section-lesson-indices']
            items = sectionTitles.map((sectionTitle, index) => {            
                let sectionLessons = lessons.slice(sectionLessonIdxs[index], sectionLessonIdxs[index + 1])                
                let sectionLessonTitles = lessonTitles.slice(sectionLessonIdxs[index], sectionLessonIdxs[index + 1])
                let sectionLessonItems = sectionLessons.map((lesson, idx) => {
                    let lessonData = curriculumData.lessonsData[lesson]
                    let lessonIsNew = lessonData  === undefined
                    if (!lessonIsNew)
                        numResourcesCounted += 1 + lessonData['other-resources'].length
                    return {lesson: lesson, lessonTitle: sectionLessonTitles[idx], isNew: lessonIsNew} 
                })
                return {sectionIndex: index, sectionLessonIndex: sectionLessonIdxs[index], sectionTitle: sectionTitle, lessonItems: sectionLessonItems}
            })
        }
        this.numLessonResourcesCounted = numResourcesCounted
        return (
            <div>
                <h4 className="header-text">Create units and create lessons inside them.</h4>
                {(isNew || sectionTitles.length <= 1) ? (<span className="form-input-description"><em className="opac">Course Plans are better organized into different units. If you do not wish to group lessons into units, you can create a single unit with a blank title.</em></span>) : null}
                <div id="creator-edit-form-plan" className="form flex-col">
                    <CoursePlanSectionInputList ref={this.curriculumFormCoursePlan} items={items} page={this} sortable={true} />
                </div>
            </div>
        )
    }
    
    renderFormResources() {
        let isNew = this.isNew
        let curriculumData = this.curriculumData        
        let resources = isNew || curriculumData['resources']
        let resourceIsExternals = isNew || curriculumData['resource-is-externals']
        return (
            <div id="creator-edit-form-resources" className="form flex-col">                
                <div className="form-item flex-col"> 
                    <label>Resources</label>
                    <h6>List general resources (not specific to any lesson) for this curriculum, such as the <strong>syllabus</strong> or links to external resources.</h6>
                    <CreatorUtility.ResourceInputList ref={this.curriculumFormResources} items={isNew ? undefined : curriculumData['resource-descriptions'].map((description, index) => {
                        let aUrl = resources[index]
                        let aResourceIsExternal = resourceIsExternals[index]                        
                        return {description: description, url: aUrl, isExternal: aResourceIsExternal, isCloudStorage: aResourceIsExternal && (!aUrl || CloudStorage.isCloudStorageURL(aUrl)), isNew: false}
                     })} sortable={true} page={this} hasCloudStorageLinkOption={true} isLessonResource={false} />
                </div>
            </div>
        )
    }


    renderFormTeacherNotes() {
        let isNew = this.isNew
        let curriculumData = this.curriculumData
        return (
            <div id="creator-edit-form-teacher-notes" className="form flex-col">
                <div className="form-item flex-col">
                    <label>Teacher Notes</label>
                    <h6>List any additional notes and tips you would like to leave for the teacher on how to deliver this curriculum.</h6>
                    <span className="form-input-description"><em className="opac">Use this list for general notes only. Leave notes and tips specific to a lesson inside the lesson, inside its <strong>Teacher Notes</strong> tab. Another great place to leave lesson-specific notes for the teacher is <strong>speaker notes</strong> in the lesson <strong>slides</strong></em>.</span>
                    <CreatorUtility.TextInputList ref={this.formTeacherNotes} items={isNew ? undefined : curriculumData['teacher-notes'].slice()} sortable={true} page={this} />
                </div>
            </div>
        )
    }

    renderFormPreviews() {  
        let isNew = this.isNew
        let curriculumData = this.curriculumData  
        return (
            <div id="creator-edit-form-previews" className="form flex-col">
                <div className="form-item flex-col">
                    <label>Content Preview Screenshots</label>
                    <h6>Please upload a single PDF or ZIP file containing several pages of screenshots of this curriculum's slides and/or other resources.</h6>
                    <span className="form-input-description"><em className="opac">Select the most prominent, representative, and impressive screenshots to include in your previews. Limit this to around 5-15 pages.</em></span>
                    <CreatorUtility.FileUploadDropZone accept={fsConfig.allowedPreviewUploadFileTypes} hasImagePreview={false} isNew={isNew} previewFile={isNew ? undefined : curriculumData.previews} newMessage={this.uploadDropZoneNewMessage.replace('@', 'a PDF or ZIP')} editMessage={this.uploadDropZoneEditMessage.replace('@', 'a PDF or ZIP')} onSuccessfulDropFn={this.curriculumPreviewsOnDropFn.bind(this)} ref={this.previewsUploadZoneRef} />
                </div>                
            </div>
        )
    }

    renderFormProof() {  
        let isNew = this.isNew
        let curriculumData = this.curriculumData
        let otherProofs = isNew || curriculumData['other-proofs']
        let proofIsExternals = isNew || curriculumData['other-proof-is-externals']
        return (
            <div id="creator-edit-form-proof" className="form flex-col">
                <div className="form-item flex-col">
                    <label>School Teacher Position Proof</label>
                    <h6>Please upload a document as a proof that you held a teaching position, during the time you taught this course curriculum, at the school you designated: such as a school contract or document, etc.</h6>
                    <CreatorUtility.FileUploadDropZone accept={fsConfig.allowedTeachingProofUploadFileTypes} hasImagePreview={false} isNew={isNew} previewFile={isNew ? undefined : curriculumData['school-proof']} newMessage={this.uploadDropZoneNewMessage.replace('@', 'a PDF or image')} editMessage={this.uploadDropZoneEditMessage.replace('@', 'a PDF or image')} onSuccessfulDropFn={this.generateCurriculumProofOnDropFn(true)} ref={this.schoolProofUploadZoneRef} />
                </div>
                <div className="form-item flex-col">
                    <label>Teaching Proof</label>
                    <h6>Please upload a document as a proof that you previously taught this curriculum at the school you designated: such as a school document listing the course with your name, an official syllabus, etc.</h6>
                    <CreatorUtility.FileUploadDropZone accept={fsConfig.allowedTeachingProofUploadFileTypes} hasImagePreview={false} isNew={isNew} previewFile={isNew ? undefined : curriculumData['teaching-proof']} newMessage={this.uploadDropZoneNewMessage.replace('@', 'a PDF or image')} editMessage={this.uploadDropZoneEditMessage.replace('@', 'a PDF or image')} onSuccessfulDropFn={this.generateCurriculumProofOnDropFn(false)} ref={this.teachingProofUploadZoneRef} />
                </div>
                <div className="form-item flex-col"> 
                    <label>Other Supporting Documents</label>
                    <h6>List any other documents that support establishing your teaching this curriculum. If you are submitting the curriculum on behalf of another teacher, add a document that states your permission from the original teacher author.</h6>
                    <CreatorUtility.ResourceInputList ref={this.otherProofsRefs} items={isNew ? undefined : curriculumData['other-proof-descriptions'].map((description, index) => {
                        return {description: description, url: otherProofs[index], isExternal: proofIsExternals[index], isNew: false}
                     })} addMessage={'Add a new proof document such as a web link, PDFs, slides, etc. ...'} isResource={false} isLessonResource={false} defaultResourceTypeIsExternal={false} includeMessageAboutExtenralResource={false} sortable={true} page={this} />
                </div>
            </div>
        )
    }

    renderFormPromos() {  
        let isNew = this.isNew
        let curriculumData = this.curriculumData
        let promoCodes = isNew || curriculumData['promo-codes']
        let promoTiers = isNew || curriculumData['promo-tiers']
        let currentPromoCode = isNew || curriculumData['promo-code-current']
        return (
            <div id="creator-edit-form-promos" className="form flex-col">                
                <div className="form-item flex-col"> 
                    <label>Promotion Codes</label>
                    <h6>Create promotion codes to offer your curriculum at a discounted price. You can set one of them to be applied automatically for everyone.</h6>
                    <PromoInputList ref={this.promosRefs} items={isNew ? undefined : promoCodes.map((promoCode, index) => {
                        return {promo: promoCode, tier: promoTiers[index], isAutomatic: currentPromoCode === promoCode, isNew: false}
                     })} addMessage={'Create a new promotion...'} sortable={true} page={this} />
                </div>
            </div>
        )
    }

    renderBody() {
        if (this.state.dataFetched) {            
            return (
                <div>
                    {this.isNew ? null : this.renderExternalIframes()}
                    <div id="creator-edit-body" className="flex-col section page-body">
                        <TopCTAComponent parent={this} ref={this.topCTAComponentRef} />
                        {this.renderForm()}
                    </div>
                </div>
            )
        } else return Utility.renderPageLoading()
    }

    // *** A *** Some of curriculum info (course and each lesson descriptions) comes from external HTML files sitting in the fileserver. We get those content by having invisible iframes to those html files, whose scripts will asyncronously send (postMessage) to this page their content DOM as a string. 
    // Here we create those iframes to initiate this process, and in another place we will receive the sent messages and convert the string into DOM elements to place in the right positions in this page...:
    renderExternalIframes() {
        return Utility.renderExternalIframes(this, this, this.curriculumData, true)
    }

    // *** B *** here we will handle/receive the sent messages and convert the string into DOM elements to place in the right positions in this page...:    
    handleReceivingContentFromExternalIframes() {
        Utility.handleReceivingContentFromExternalIframesForTextEditor(this, this.id)
    }
}

class TopCTAComponent extends React.Component {
    constructor(props) { 
        super(props)
        this.parent = props.parent
        this.state = {      
            dataFetched: true
        }
    }
    
    render() {
        let parent = this.parent
        let isNew = parent.isNew
        let curriculumData = parent.curriculumData
        let active = isNew ? false : curriculumData.active        
        return (
            <div id="creator-edit-top-section" className="page-body-top-listing section">
                <div className="flex-start flex-col">
                    <div className="bottom-margin25">
                        <i className="fas fa-angle-left row-padding-10"></i>    
                        {Utility.renderButton('Library', 'creator-edit-back-button', 'navigation-button text-button button', (e) => parent.promptForSaveBeforeNavigatingAway('/creator/library'))}
                    </div>
                    {isNew ? null : Utility.renderButton(curriculumData.title, 'top-curriculum-listing-view-link', 'button text-button h3size left-margin0 bottom-margin25', (e) => parent.promptForSaveBeforeNavigatingAway('/creator/view?subject=' + curriculumData.subject + '&id=' + curriculumData.id + '&random-key=' + curriculumData['random-key']))}
                    <h2 className="header-text size31">{isNew ? 'Create' : (parent.isInDraftStage ? 'Edit' : 'Manage')} your curriculum</h2>
                </div>
                <div id="creator-edit-submit" className="flex-col form-side-panel curriculum-listings-side-panel opac-background">
                    {parent.isInDraftStage ? Utility.renderButtonFromButton(<span><i className="fas fa-save"></i>&nbsp;&nbsp;&nbsp;Save curriculum draft</span>, 'creator-edit-save-draft-button', 'wide-cta-text-button cta-text-button clear-shadowed-text-button bordered-text-button text-button button', (e) => parent.saveForm()) : null}
                    {Utility.renderButtonFromButton(<span><i className="fas fa-paper-plane"></i>&nbsp;&nbsp;&nbsp;{'Submit curriculum' + (parent.isInDraftStage ? '' : ' update')}</span>, 'creator-edit-submit-button', 'wide-cta-text-button cta-text-button shadowed-text-button bordered-text-button text-button button', (e) => parent.submitForm())}
                    {parent.isInProductions ? Utility.renderButtonFromButton(<span><i className={active ? 'fas fa-toggle-off' : 'fas fa-toggle-on'}></i>&nbsp;&nbsp;&nbsp;{active ? 'Remove from store' : 'Put back in store'}</span>, 'creator-edit-toggle-active-button', 'wide-cta-text-button cta-text-button' + (active ? ' red-shadowed-text-button' : '') + ' clear-shadowed-text-button bordered-text-button text-button button', (e) => parent.toggleCurriculumActive()) : null}
                    {parent.isInSubmissions ? Utility.renderButtonFromButton(<span><i className="fas fa-eject"></i>&nbsp;&nbsp;&nbsp;Remove submission</span>, 'creator-edit-remove-from-store-button', 'wide-cta-text-button cta-text-button red-shadowed-text-button clear-shadowed-text-button bordered-text-button text-button button', (e) => parent.removeCurriculumFromStore()) : null}
                    {parent.isInDrafts ? Utility.renderButtonFromButton(<span><i className="fas fa-trash-alt"></i>&nbsp;&nbsp;&nbsp;Delete curriculum</span>, 'creator-edit-delete-button', 'wide-cta-text-button cta-text-button red-shadowed-text-button clear-shadowed-text-button bordered-text-button text-button button', (e) => parent.deleteCurriculum()) : null}                           
                </div>
            </div>
        )
    }
}

class SubjectOptions extends Utility.SubjectOptions {
    constructor(props) {
        super(props)
        this.page = props.page
        this.defaultValue = props.defaultValue || ''
        this.label = props.label || 'Choose subject'
    }

    // if the search subject pulldown menu is changed...
    subjectOnChangeFn(e, val) {
        if (val) {
            let subject = val
            let isPropose = subject === 'propose'
            let topics 
            this.page.setSubject(isPropose ? undefined : subject)            
            if (isPropose) {                
                topics = []
                this.page.formProposedSubjectInputRef.current.className = 'item-right-of-item'
            } else {
                this.page.formProposedSubjectInputRef.current.className = 'hidden ' + this.page.formProposedSubjectInputRef.current.className
                topics = Database.getSubjectTopics(subject) || []
            }
            this.page.formProposedTopicInputRef.current.className = 'hidden ' + this.page.formProposedTopicInputRef.current.className
            // reset the chosen topic 
            this.page.topicSelectComponentRef.current.resetToSubject(subject, topics)            
        }
    }
    
    render() {        
        this.subjects = this.state.dataFetched ? Database.getSubjects() : []        
        return (        
            <Utility.OptionsSelect ref={this.optionsSelectComponent} label={this.label} id={'curriculum-form-subject-select'} containerExtraClasses={'form-select-div'} optionsDivExtraClasses={'form-'} options={[...this.subjects, 'propose']} optionKeyToTitleFn={(idx, s) => { return s === 'propose' ? 'Propose new subject' : Database.getSubjectTitle(s)}} onChangeFn={this.subjectOnChangeFn.bind(this)} defaultValue={this.defaultValue} addDefault={false} disabled={this.props.disabled || false} />
        )
    }
}

class TopicOptions extends Utility.SubjectOptions {
    constructor(props) {
        super(props) 
        this.state = {
            dataFetched: Database.isTopicsDataFetched(),
            subject: undefined,
            topics: props.topics || [],
            label: props.label || 'Choose topic'
        }
        this.page = props.page
        this.defaultValue = props.defaultValue || ''
    }

    componentDidUpdate(prevProps, prevState, snapshot) {        
        this.optionsSelectComponent.current.setState({options: [...this.state.topics, 'propose']})
    }
    
    // resets the topic options select component (when user chose a new subject from the subject select component):
    resetToSubject(subject, topics) {
        this.defaultValue = ''        
        this.page.topic = undefined
        this.optionsSelectComponent.current.setValue('')
        this.setState({label: 'Choose topic', subject: subject, topics: topics})
    }

    // if the search subject pulldown menu is changed...
    topicOnChangeFn(e, val) {        
        if (val) {
            let topic = val
            let isPropose = topic === 'propose'
            let setTopic = undefined            
            if (isPropose) {
                this.page.formProposedTopicInputRef.current.className = 'item-right-of-item'
            } else {
                setTopic = topic
                this.page.formProposedTopicInputRef.current.className = 'hidden ' + this.page.formProposedTopicInputRef.current.className
            }
            this.page.topic = setTopic
        }
    }

    render() {        
        // adding key to ensure renders fresh on a new label:
        return (
            <Utility.OptionsSelect ref={this.optionsSelectComponent} key={this.state.label} label={this.state.label} id={'curriculum-form-topic-select'} containerExtraClasses={'form-select-div'} options={[...this.state.topics, 'propose']} optionKeyToTitleFn={(idx, s) => { return s === 'propose' ? 'Propose new topic' : Database.getTopicTitle(s)}} onChangeFn={this.topicOnChangeFn.bind(this)} defaultValue={this.defaultValue} addDefault={false} />
        )
    }
}

class LanguageOptions extends Utility.OptionsSelect {
    constructor(props) {
        super(props)        
        this.page = props.page
    }

    // if the language pulldown menu is changed...
    onChangeFn(e, val) {            
        if (val) {
            let language = val        
            let isPropose = language === 'propose' 
            let setLanguage = undefined
            if (isPropose) {
                this.page.formProposedLanguageInputRef.current.className = 'item-right-of-item'
            } else {
                setLanguage = language
                this.page.formProposedLanguageInputRef.current.className = 'hidden ' + this.page.formProposedLanguageInputRef.current.className
            }
            this.page.language = setLanguage
        }
    }

}

// editor for list of sections in the course plan:
class CoursePlanSectionInputList extends CreatorUtility.TextInputList {

    constructor(props) {
        super(props)
        this.addMessage = 'Add new unit title...'     
    }

    reportCountChange(index, countChange) {
        if (index < this.state.items.length - 1) {
            const { items } = this.state
            // tell sections that fall after the changing section about the sectionLessonIndex change (going up or down by 1) due to add/delete a lesson:
            Utility.arrayFromTo(index + 1, this.state.items.length - 1).forEach(idx => {                
                items[idx].sectionLessonIndex += countChange         
                this.itemElements[idx].current.setState({focus: false, sectionLessonIndex: items[idx].sectionLessonIndex})
            })
            this.setState({items: items})
        }        
    } 

    generateNewItem(index, count) {        
        return {sectionIndex: index, sectionLessonIndex: count, sectionTitle: '', lessons: []}
    }

    handleNewItem() {
        // make sure a subject is chosen so the curriculum id is set:
        if (!(this.page.subject || this.page.formProposedSubjectInputRef.current.value)) {
            Utility.showTextDialog(0, 'Choose a subject first.', generalConfig.dialogBoxErrorIconClassName)
            return
        }
        if (this.page.isNew) {
            Utility.showTextDialog(0, 'Please save your curriculum first before starting on your course plan.', generalConfig.dialogBoxErrorIconClassName)
            return
        }
        const { items } = this.state
        let count = this.itemElements.reduce((acc, curr) => acc += curr.current.inputElement.current.state.items.length, 0)
        this.itemElements.map(i => i.current.setState({focus: false}))
        this.itemElements.push(React.createRef())
        this.setState({items: [...items, this.generateNewItem(items.length, count)], focus: true })
    }

    handleDelete(index) {
        // prompt user to confirm...:
        let item = this.itemElements[index].current.getValue()
        let deleteFn = () => {
            super.handleDelete(index)            
        }
        let lessons = item.lessonItems.filter(i => !i.isNew).map(i => i.lesson)
        let hasEditedLessons = lessons.length > 0
        let deleteLessonsFn = () => {
            deleteFn()
            if (hasEditedLessons)
                this.page.deleteLessons(lessons)
            return true
        }
        Utility.showTextDialog(0, 'This will delete the entire section' + (hasEditedLessons ? ', which includes lessons you previously created. They will be permanently deleted.' : '.') + ' \nAre you sure?', generalConfig.dialogBoxQuestionIconClassName, undefined, ['Cancel', 'Delete'], [undefined, deleteLessonsFn])
    }

    renderOneItem(index, key, value) {
        return (
            <CoursePlanSectionInput
                key={key}
                getKey={key}
                index={index}
                value={value}
                onDelete={(index) => this.handleDelete(index)}
                autoFocus={this.state.focus && index == this.state.items.length - 1}
                ref={this.itemElements[index]}
                parent={this}                
            />
        )
    }    
}

// editor for one section (contains a list of lessons) in the course plan:
class CoursePlanSectionInput extends CreatorUtility.TextInputListItem {
    
    constructor(props) {
        super(props)
        this.state = {
            sectionLessonIndex: this.props.value.sectionLessonIndex
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.haveToReportCountChange) {
            this.haveToReportCountChange = false
            // tell the section list (parent) that my count of lessons have changed (so other sections can adjust their sectionLessonIndex values):
            this.parent.reportCountChange(this.props.index, this.state.countChange)
        }
        this.inputElement.current.setState({focus: this.state.focus, sectionLessonIndex: this.state.sectionLessonIndex})
    }
    
    getValue() {
        return {sectionLessonIndex: this.state.sectionLessonIndex, sectionTitle: this.inputElement.current.getSectionTitle(), lessonItems: this.inputElement.current.getList()}
    }
    
    itemExtraClassNames() { return '' }

    renderInputComponent() {
        return (
            <CoursePlanLessonInputList sectionIndex={this.props.value.sectionIndex} sectionLessonIndex={this.state.sectionLessonIndex} sectionTitle={this.props.value.sectionTitle} items={this.props.value.lessonItems} ref={this.inputElement} autoFocus={this.props.autoFocus} addNewButtonExtraClasses={'top-margin10'} parent={this} page={this.page} sortable={true} />
        )
    }   
}

// editor for a list of lessons in a given section of the course plan:
class CoursePlanLessonInputList extends CreatorUtility.TextInputList {
    constructor(props) {
        super(props)
        this.state.sectionIndex = props.sectionIndex   
        this.state.sectionLessonIndex = props.sectionLessonIndex
        this.parent = props.parent
        this.sectionTitleInputElement = React.createRef()
        this.curriculum = this.page.id   
        this.addMessage = 'Add new lesson title...'     
    }
    
    componentDidUpdate(prevProps, prevState, snapshot) {
        //super.componentDidUpdate(prevProps, prevState, snapshot)
        // tell the section list (parent) that my count of lessons have changed (so other sections can adjust their sectionLessonIndex values):  
        if (this.haveToReportCountChange !== undefined) {
            let countChange = this.haveToReportCountChange
            delete this.haveToReportCountChange
            this.parent.haveToReportCountChange = true
            this.parent.setState({focus: true, count: this.state.items.length, countChange: countChange})
        }
    }
    
    generateNewItem(defaultInputValue) {
        // generate a unique id for the lesson:
        let lesson = this.curriculum + '-' + CreatorUtility.generateUniqueId(false, 8)
        return {lesson: lesson, lessonTitle: defaultInputValue || '', isNew: true}
    } 

    handleNewItem() {        
        super.handleNewItem()
        this.haveToReportCountChange = 1
    }

    handleDelete(index) {
        // if it was a previously created lesson, prompt user to confirm first...:        
        let item = this.itemElements[index].current.getValue()        
        let deleteFn = () => {
            super.handleDelete(index)
            this.haveToReportCountChange = -1  
        }
        if (item.isNew)
            deleteFn()
        else {
            let deleteLessonFn = () => {
                deleteFn()
                this.page.deleteLessons([item.lesson])
            }
            Utility.showTextDialog(0, 'You previously created this lesson. This will permanently delete it.\nAre you sure?', generalConfig.dialogBoxQuestionIconClassName, undefined, ['Cancel', 'Delete'], [undefined, deleteLessonFn])
        }
    }

    getSectionTitle() {
        return this.sectionTitleInputElement.current.value
    }

    renderOneItem(index, key, value) {
        return (
            <CoursePlanLessonInput
                key={key}
                getKey={key}
                index={index}
                sectionIndex={this.state.sectionIndex}
                sectionLessonIndex={this.state.sectionLessonIndex}
                value={value}
                onDelete={(index) => this.handleDelete(index)}
                autoFocus={this.state.focus && index == this.state.items.length - 1}
                ref={this.itemElements[index]}
                parent={this}
            />)
    }

    render() {
        let itemComponents = super.render()
        return (
          <div className="lesson-input-list complex-level-1-input-list-item boxed-div flex-col">
            <label>Unit Title</label>
            {this.props.autoFocus ?
              (<input placeholder="Enter title of the unit" defaultValue={this.props.sectionTitle} ref={this.sectionTitleInputElement} className="course-plan-section-title-input" autoFocus />) :
                (<input placeholder="Enter title of the unit" defaultValue={this.props.sectionTitle} ref={this.sectionTitleInputElement} 
                className="course-plan-section-title-input" />)}
            <label>Lessons</label>
            {itemComponents}
          </div>
        )
    }
}

// editor for a lesson in a given section in a course plan:
class CoursePlanLessonInput extends CreatorUtility.TextInputListItem {

    constructor(props) {
        super(props)
        this.value = this.props.value
        this.lesson = this.value.lesson
        this.isNew = this.value.isNew
    }

    getValue() {
        return {lesson: this.lesson, lessonTitle: super.getValue(), isNew: this.isNew}
    }

    // if we're gonig to edit lesson page, save the curriculum data before navigating:
    lessonEditButtonClicked(sectionNumber, lessonNumber, weekNumber, dayNumber, lessonTitle) {
        if (!lessonTitle) {
            Utility.showTextDialog(0, 'Enter a title for the lesson first', generalConfig.dialogBoxErrorIconClassName)
            return
        }        
        let page = this.page        
        let afterSaveFn = () => {
            page.setState({navigateTo: '/creator/edit-lesson?subject=' + page.subject + '&id=' + page.id + '&random-key=' + page.curriculumData['random-key'] + '&section=' + sectionNumber + '&week=' + weekNumber + '&day=' + dayNumber + '&lesson=' + this.lesson + '&number=' + lessonNumber})
        }
        let error = page.saveForm(afterSaveFn, true)          
        // if there was an error saving the form:
        if (error)
            Utility.showTextDialog(0, 'Failed to save curriculum due to errors in the form:\n"' + error + '"\nPlease correct the errors in the curriculum form and save before editing a lesson.', generalConfig.dialogBoxErrorIconClassName)       
    }
    
    // copies the lesson title/data to the clipboard:
    handleLessonCopy() {        
        this.page.copyLessonToClipboard(this.getValue())
    }

    // pastes the lesson title/data from the clipboard copy to this (new) lesson:
    
    async handleLessonPaste() {
        let page = this.page
        let copy = page.lessonCopiedToClipboard        
        if (copy !== undefined) {            
            let proceedWithPaste = async (copyResourcesToo) => {
                await page.pasteLessonFromClipboard(this, copyResourcesToo)
            }            
            // if copied lesson has uploaded resources, first prompt the user if wants to copy any uploaded lesson slides/resources or not... :
            if (CreatorUtility.doesLessonHaveUploadedResources(page.curriculumData.lessonsData[copy.lesson]))
                Utility.showTextDialog(0, 'Do you also want to make copies of any uploaded lesson slides/resources?', generalConfig.dialogBoxQuestionIconClassName, undefined, ['Yes', 'No'], [async () => await proceedWithPaste(true), async () => await proceedWithPaste(false)])
            else
                await proceedWithPaste(false)
        }
    }
    
    // called once paste a copy onto this completes: sets the title and isnew=false
    onPasteSuccess(title) {
        this.isNew = false
        this.inputElement.current.value = 'Copy of ' + title
    }

    itemExtraClassNames() { return '' }

    renderInputComponent() {
        let isNew = this.isNew
        let sectionNumber = this.props.sectionIndex + 1
        let numberInSection = this.props.index + 1
        let lessonNumber = this.props.sectionLessonIndex + numberInSection
        let numberOfLessonsPerWeek = this.page.curriculumData['num-lessons-per-week']
        let lessonIndex = lessonNumber - 1
        let weekIndex = Math.floor(lessonIndex / numberOfLessonsPerWeek)
        let weekNumber = weekIndex + 1
        let dayNumber = lessonIndex - (weekIndex * numberOfLessonsPerWeek) + 1
        return (
            <div className="full-width flex-row flex-center">
                <label className="h5size course-plan-lesson-number-label">Lesson {lessonNumber}</label>
                {this.props.autoFocus ?
                    (<input placeholder="Enter title of the lesson" defaultValue={this.value.lessonTitle} ref={this.inputElement} autoFocus />) :
                    (<input placeholder="Enter title of the lesson" defaultValue={this.value.lessonTitle} ref={this.inputElement} />)}
                {Utility.renderButton((isNew ? 'Create' : 'Edit') + ' lesson', 'creator-edit-edit-lesson-button-' + lessonNumber, 'clear-shadowed-text-button bordered-text-button superwide-text-button text-button button', (e) => this.lessonEditButtonClicked(sectionNumber, lessonNumber, weekNumber, dayNumber, super.getValue()), {tabIndex: '-1'})}
            </div>
        )
    }

    renderActionButtons() {
        let page = this.page
        let isNew = this.isNew
        let numberInSection = this.props.index + 1
        let lessonNumber = this.props.sectionLessonIndex + numberInSection
        let lessonActionsMenuOptions = {}             
        let lessonActionsMenuOptionIcons = {} 
        // add lesson action button menu items:
        if (!isNew) {
            lessonActionsMenuOptions['Copy lesson'] = (e) => { this.handleLessonCopy() }
            lessonActionsMenuOptionIcons['Copy lesson'] = 'fas fa-copy'
        }
        if (isNew && page.lessonCopiedToClipboard) {
            lessonActionsMenuOptions['Paste lesson'] = async (e) => { await this.handleLessonPaste() }
            lessonActionsMenuOptionIcons['Paste lesson'] = 'fas fa-paste'
        }
        lessonActionsMenuOptions['Delete lesson'] = (e) => { this.handleDelete() }
        lessonActionsMenuOptionIcons['Delete lesson'] = 'fas fa-times'
        return (
            <div className="flex-row flex-center">
                <Utility.ButtonOrLinkMenuSelect id={'creator-edit-edit-lesson-button-' + lessonNumber + '-actions-menu'} key={Utility.getEpochTime()} options={lessonActionsMenuOptions} optionKeyToTitleIconFn={lessonActionsMenuOptionIcons} allOptionsAreButtons={true} selectButtonIcon={'fas fa-ellipsis-v'} containerExtraClasses={'form-row-padded-item'} optionsDivExtraClasses={'lesson-actions-select-menu'} noBackgroundLayer={true} />                
            </div>
        )
    }
}

// editor for a promotion list teacher creates for a curriculum
export class PromoInputList extends CreatorUtility.TextInputList {
    constructor(props) {
        super(props)
        this.addNewButtonExtraClasses = 'promo-input-list-add-new-item'        
    }
    
    // called when an item's isAutomatic checkbox is changed. when one is turned on, we need to turn any on other one off, because only one at a time can be on:
    isAutomaticCheckboxClicked(index, on) {
        if (on) {
            // turn off another item if on:
            this.itemElements.forEach((i, idx) => {
                if (idx !== index) {
                    let checkbox = i.current.isAutomaticCheckboxRef.current
                    if (Utility.isCheckboxChecked(checkbox))                        
                        Utility.toggleCheckbox(checkbox)
                }
            })
        }
    }

    // called when teacher clicks on button to get share link for a promo. if curriculum is live, copy share link to clipboard:
    promoGetShareLinkButtonClicked(promoCode) {
        let page = this.page
        if (page.curriculumData.active)
            Utility.copyToClipboard(page.getCurriculumShareUrl() + '?promo=' + promoCode)
        else
            Utility.showTextDialog(0, 'Once your curriculum is live on the store, use this button to get a share link for your promotion.', generalConfig.dialogBoxInfoIconClassName)
    }

    generateNewItem(defaultInputValue) {
        return defaultInputValue || {promo: CreatorUtility.generateUniqueId(true, payConfig.promoCodeLength).toUpperCase(), tier: 0, isAutomatic: false, isNew: true}
    }
  
    renderOneItem(index, key, value) {    
      return (<PromoInputListItem
          key={key}
          getKey={key}
          index={index}
          value={value}          
          onDelete={(index) => this.handleDelete(index)}
          autoFocus={this.state.focus && index == this.state.items.length - 1}
          sortable={true}
          parent={this}
          ref={this.itemElements[index]} />)
    }
  }
  
// editor for a promotion teacher created for a curriculum:
class PromoInputListItem extends CreatorUtility.TextInputListItem {
  
    constructor(props) {
        super(props)
        this.value = this.props.value        
        this.tierOptionsSelectRef = React.createRef()
        this.isAutomaticCheckboxRef = React.createRef()
    }
  
    getValue() {
        return {promo: this.getPromoCode(), tier: this.getTier(), isAutomatic: this.isAutomatic()}
    }
    
    getPromoCode() { return this.inputElement.current.value }

    getTier() {
        let tierOptionSelect = this.tierOptionsSelectRef.current
        return tierOptionSelect ? tierOptionSelect.getValue() : this.value.tier
    }

    isAutomatic() { 
      let checkbox = this.isAutomaticCheckboxRef.current
      return checkbox ? Utility.isCheckboxChecked(this.isAutomaticCheckboxRef.current) : this.value.isAutomatic
    }
    
    itemExtraClassNames() { return '' }  
    
    renderInputComponent() {
        let isNew = this.isNew
        let promoTierPercents = payConfig.promoTierDiscountPercents
        let promoCodeLength = '' + payConfig.promoCodeLength
        let tier = isNew || this.getTier()
        let index = this.index
        return (
            <div className="full-width flex-row flex-start">
                {this.props.autoFocus ?
                    <input className="short-width-input text-input-list-item-element left-margin0" defaultValue={this.props.value.promo} maxLength={promoCodeLength} ref={this.inputElement} autoFocus /> : 
                    <input className="short-width-input text-input-list-item-element left-margin0"  defaultValue={this.props.value.promo} maxLength={promoCodeLength} ref={this.inputElement} />}
                <Utility.OptionsSelect  key={Utility.getEpochTime()} label={(isNew || !tier) ? 'Choose % off' : (promoTierPercents[tier] + '% off')} id={'curriculum-form-promo-tier-' + index} containerExtraClasses={'form-select-div text-input-list-item-element short-select-div'} optionsDivExtraClasses={'short-select-div-options'} options={ [...Array(promoTierPercents.length).keys()].slice(1)} optionKeyToTitleFn={(idx, p) => { return promoTierPercents[p] + '% off' }} onChangeFn={(e, val) => { this.tierOptionsSelectRef.current.setValue(val) }} defaultValue={isNew ? undefined : tier} addDefault={false} ref={this.tierOptionsSelectRef} />
                {Utility.renderCheckbox('promo-automatic-checkbox-' + index, this.isAutomatic(), 'Apply for everyone', this.isAutomaticCheckboxRef, 'text-input-list-item-element', undefined, undefined, (on) => this.parent.isAutomaticCheckboxClicked(index, on))}
                {Utility.renderButton('Share Link', 'curriculum-form-promo-share-link-' + index, 'flex-end-item clear-shadowed-text-button bordered-text-button superwide-text-button text-button button', (e) => this.parent.promoGetShareLinkButtonClicked(this.getPromoCode()), {tabIndex: '-1'})}
            </div>
        )
    }  
}
  
export default CreatorEditCurriculumPage
